import { useEffect, useMemo, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { PageHeader } from "@/components/DashboardLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from "@/components/ui/dialog";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from "@/components/ui/table";
import { toast } from "sonner";
import { Ticket, Pencil, Trash2, Plus } from "lucide-react";

const EMPTY_FORM = {
    code: "",
    discount_type: "percent",
    discount_value: 10,
    applies_to: "all",
    max_uses: "",
    expires_at: "",
    active: true,
    duration: "once",
};

const APPLIES_LABEL = {
    all: "Anywhere",
    signup: "Signup",
    membership: "Membership",
    deal: "Deal checkout",
};

function formatDiscount(p) {
    if (p.discount_type === "founder") return "Free membership";
    return p.discount_type === "percent"
        ? `${p.discount_value}% off`
        : `HK$${p.discount_value.toFixed(0)} off`;
}

export default function AdminPromoCodes() {
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [editing, setEditing] = useState(null); // null = new, object = edit
    const [form, setForm] = useState(EMPTY_FORM);
    const [saving, setSaving] = useState(false);

    const load = async () => {
        setLoading(true);
        try {
            const { data } = await api.get("/admin/promo-codes");
            setRows(data);
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        load();
    }, []);

    const stats = useMemo(() => {
        const active = rows.filter((r) => r.active).length;
        const totalRedemptions = rows.reduce((s, r) => s + (r.uses || 0), 0);
        return { total: rows.length, active, totalRedemptions };
    }, [rows]);

    const openNew = () => {
        setEditing(null);
        setForm(EMPTY_FORM);
        setDialogOpen(true);
    };

    const openEdit = (row) => {
        setEditing(row);
        setForm({
            code: row.code,
            discount_type: row.discount_type,
            discount_value: row.discount_value,
            applies_to: row.applies_to || "all",
            max_uses: row.max_uses ?? "",
            expires_at: row.expires_at || "",
            active: row.active,
            duration: row.duration || "once",
        });
        setDialogOpen(true);
    };

    const isFounder = form.discount_type === "founder";

    const changeType = (v) => {
        if (v === "founder") {
            // Founder codes give away paid membership, so default to single-use.
            setForm({ ...form, discount_type: v, max_uses: form.max_uses === "" ? 1 : form.max_uses });
        } else {
            const value = Number(form.discount_value) > 0 ? form.discount_value : 10;
            const applies = isFounder ? "all" : form.applies_to;
            setForm({ ...form, discount_type: v, discount_value: value, applies_to: applies });
        }
    };

    const save = async () => {
        setSaving(true);
        try {
            const payload = {
                code: form.code.trim().toUpperCase(),
                discount_type: form.discount_type,
                discount_value: isFounder ? 0 : Number(form.discount_value),
                applies_to: isFounder ? "signup" : form.applies_to,
                max_uses: form.max_uses === "" ? null : Number(form.max_uses),
                expires_at: form.expires_at || null,
                active: form.active,
                duration: form.duration,
            };
            if (editing) {
                await api.patch(`/admin/promo-codes/${editing.id}`, payload);
                toast.success(`Promo ${payload.code} updated`);
            } else {
                await api.post("/admin/promo-codes", payload);
                toast.success(`Promo ${payload.code} created`);
            }
            setDialogOpen(false);
            await load();
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setSaving(false);
        }
    };

    const remove = async (row) => {
        if (!window.confirm(`Delete promo code ${row.code}? This cannot be undone.`)) return;
        try {
            await api.delete(`/admin/promo-codes/${row.id}`);
            toast.success(`Promo ${row.code} deleted`);
            await load();
        } catch (e) {
            toast.error(formatApiError(e));
        }
    };

    return (
        <>
            <PageHeader
                eyebrow="Marketing"
                title="Promo codes"
                description="Create, manage and monitor promo codes used across signup, memberships, and deal checkouts."
                testid="admin-promo-header"
                action={
                    <Button
                        onClick={openNew}
                        data-testid="promo-new-btn"
                        className="rounded-full bg-gold-500 hover:bg-gold-600 text-navy-900 font-semibold h-11 px-5"
                    >
                        <Plus size={16} className="mr-2" /> New promo code
                    </Button>
                }
            />

            <div className="px-6 lg:px-12 py-8 space-y-6">
                <div className="grid sm:grid-cols-3 gap-5">
                    <StatCard label="Total codes" value={stats.total} testid="promo-stat-total" />
                    <StatCard label="Active codes" value={stats.active} testid="promo-stat-active" />
                    <StatCard
                        label="Redemptions"
                        value={stats.totalRedemptions}
                        accent
                        testid="promo-stat-redemptions"
                    />
                </div>

                <div className="rounded-2xl bg-white border border-navy-100/70 shadow-soft overflow-hidden">
                    <Table data-testid="promo-table">
                        <TableHeader>
                            <TableRow className="bg-cream hover:bg-cream">
                                <TableHead className="text-navy-700 uppercase text-xs tracking-[0.2em]">
                                    Code
                                </TableHead>
                                <TableHead className="text-navy-700 uppercase text-xs tracking-[0.2em]">
                                    Discount
                                </TableHead>
                                <TableHead className="text-navy-700 uppercase text-xs tracking-[0.2em]">
                                    Applies to
                                </TableHead>
                                <TableHead className="text-navy-700 uppercase text-xs tracking-[0.2em]">
                                    Uses
                                </TableHead>
                                <TableHead className="text-navy-700 uppercase text-xs tracking-[0.2em]">
                                    Expires
                                </TableHead>
                                <TableHead className="text-navy-700 uppercase text-xs tracking-[0.2em]">
                                    Status
                                </TableHead>
                                <TableHead className="text-right" />
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {loading && (
                                <TableRow>
                                    <TableCell colSpan={7} className="text-center text-navy-500 py-10">
                                        Loading…
                                    </TableCell>
                                </TableRow>
                            )}
                            {!loading && rows.length === 0 && (
                                <TableRow>
                                    <TableCell colSpan={7} className="text-center text-navy-500 py-10">
                                        <div className="flex flex-col items-center gap-2">
                                            <Ticket className="text-gold-500" />
                                            <div>No promo codes yet. Create your first one to start driving conversions.</div>
                                        </div>
                                    </TableCell>
                                </TableRow>
                            )}
                            {rows.map((r) => (
                                <TableRow key={r.id} data-testid={`promo-row-${r.code}`}>
                                    <TableCell className="font-mono uppercase text-navy-700 font-semibold">
                                        {r.code}
                                    </TableCell>
                                    <TableCell className="text-navy-700">{formatDiscount(r)}</TableCell>
                                    <TableCell className="text-navy-500">
                                        <Badge className="bg-navy-50 text-navy-700 border-navy-100 border">
                                            {APPLIES_LABEL[r.applies_to] || r.applies_to}
                                        </Badge>
                                    </TableCell>
                                    <TableCell className="text-navy-700">
                                        {r.uses}
                                        {r.max_uses != null && (
                                            <span className="text-navy-500"> / {r.max_uses}</span>
                                        )}
                                    </TableCell>
                                    <TableCell className="text-navy-500">
                                        {r.expires_at || "—"}
                                    </TableCell>
                                    <TableCell>
                                        {r.active ? (
                                            <Badge className="bg-emerald-100 text-emerald-800 border-emerald-200 border-0">
                                                ACTIVE
                                            </Badge>
                                        ) : (
                                            <Badge className="bg-navy-50 text-navy-500 border-navy-100 border-0">
                                                PAUSED
                                            </Badge>
                                        )}
                                    </TableCell>
                                    <TableCell className="text-right">
                                        <div className="flex justify-end gap-2">
                                            <Button
                                                variant="ghost"
                                                size="sm"
                                                data-testid={`promo-edit-${r.code}`}
                                                onClick={() => openEdit(r)}
                                                className="text-navy-700 hover:text-navy-900"
                                            >
                                                <Pencil size={14} />
                                            </Button>
                                            <Button
                                                variant="ghost"
                                                size="sm"
                                                data-testid={`promo-delete-${r.code}`}
                                                onClick={() => remove(r)}
                                                className="text-red-600 hover:text-red-800 hover:bg-red-50"
                                            >
                                                <Trash2 size={14} />
                                            </Button>
                                        </div>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </div>
            </div>

            <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
                <DialogContent
                    data-testid="promo-dialog"
                    className="max-w-lg bg-white"
                >
                    <DialogHeader>
                        <DialogTitle className="font-display text-2xl text-navy-700">
                            {editing ? `Edit ${editing.code}` : "New promo code"}
                        </DialogTitle>
                        <DialogDescription>
                            Codes are case-insensitive. Percent codes cap at 100%; fixed codes are in HKD.
                        </DialogDescription>
                    </DialogHeader>

                    <div className="grid gap-4">
                        <div>
                            <Label className="text-navy-700 text-xs uppercase tracking-widest">Code</Label>
                            <Input
                                value={form.code}
                                data-testid="promo-form-code"
                                onChange={(e) =>
                                    setForm({ ...form, code: e.target.value.toUpperCase() })
                                }
                                placeholder="WELCOME20"
                                className="mt-1 font-mono uppercase"
                            />
                        </div>

                        <div className="grid grid-cols-2 gap-3">
                            <div className={isFounder ? "col-span-2" : undefined}>
                                <Label className="text-navy-700 text-xs uppercase tracking-widest">
                                    Type
                                </Label>
                                <Select value={form.discount_type} onValueChange={changeType}>
                                    <SelectTrigger data-testid="promo-form-type" className="mt-1">
                                        <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="percent">Percent (%)</SelectItem>
                                        <SelectItem value="fixed">Fixed (HK$)</SelectItem>
                                        <SelectItem value="founder">Founder (free membership)</SelectItem>
                                    </SelectContent>
                                </Select>
                                {isFounder && (
                                    <p data-testid="promo-form-founder-note" className="text-xs text-navy-500 mt-2">
                                        Signup only. The new account gets an active membership
                                        straight away, with no Stripe payment.
                                    </p>
                                )}
                            </div>
                            {!isFounder && (
                            <div>
                                <Label className="text-navy-700 text-xs uppercase tracking-widest">
                                    Value
                                </Label>
                                <Input
                                    type="number"
                                    min={1}
                                    step={1}
                                    max={form.discount_type === "percent" ? 100 : undefined}
                                    value={form.discount_value}
                                    data-testid="promo-form-value"
                                    onChange={(e) =>
                                        setForm({ ...form, discount_value: e.target.value })
                                    }
                                    className="mt-1"
                                />
                            </div>
                            )}
                        </div>

                        {!isFounder && (
                        <div className="grid grid-cols-2 gap-3">
                            <div>
                                <Label className="text-navy-700 text-xs uppercase tracking-widest">
                                    Applies to
                                </Label>
                                <Select
                                    value={form.applies_to}
                                    onValueChange={(v) => setForm({ ...form, applies_to: v })}
                                >
                                    <SelectTrigger data-testid="promo-form-applies" className="mt-1">
                                        <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="all">Anywhere</SelectItem>
                                        <SelectItem value="signup">Signup</SelectItem>
                                        <SelectItem value="membership">Membership</SelectItem>
                                        <SelectItem value="deal">Deal checkout</SelectItem>
                                    </SelectContent>
                                </Select>
                            </div>
                            <div>
                                <Label className="text-navy-700 text-xs uppercase tracking-widest">
                                    Duration (Stripe)
                                </Label>
                                <Select
                                    value={form.duration}
                                    onValueChange={(v) => setForm({ ...form, duration: v })}
                                >
                                    <SelectTrigger data-testid="promo-form-duration" className="mt-1">
                                        <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="once">Once (first invoice)</SelectItem>
                                        <SelectItem value="forever">Forever (recurring)</SelectItem>
                                    </SelectContent>
                                </Select>
                            </div>
                        </div>
                        )}

                        <div className="grid grid-cols-2 gap-3">
                            <div>
                                <Label className="text-navy-700 text-xs uppercase tracking-widest">
                                    Max uses
                                </Label>
                                <Input
                                    type="number"
                                    min={1}
                                    placeholder="Unlimited"
                                    data-testid="promo-form-max-uses"
                                    value={form.max_uses}
                                    onChange={(e) => setForm({ ...form, max_uses: e.target.value })}
                                    className="mt-1"
                                />
                            </div>
                            <div>
                                <Label className="text-navy-700 text-xs uppercase tracking-widest">
                                    Expires
                                </Label>
                                <Input
                                    type="date"
                                    data-testid="promo-form-expires"
                                    value={form.expires_at || ""}
                                    onChange={(e) => setForm({ ...form, expires_at: e.target.value })}
                                    className="mt-1"
                                />
                            </div>
                        </div>

                        <div className="flex items-center gap-3">
                            <Switch
                                checked={form.active}
                                data-testid="promo-form-active"
                                onCheckedChange={(v) => setForm({ ...form, active: v })}
                            />
                            <div>
                                <div className="text-sm text-navy-700 font-medium">Active</div>
                                <div className="text-xs text-navy-500">
                                    Only active codes can be redeemed.
                                </div>
                            </div>
                        </div>
                    </div>

                    <DialogFooter>
                        <Button
                            variant="outline"
                            data-testid="promo-form-cancel"
                            onClick={() => setDialogOpen(false)}
                            className="rounded-full"
                        >
                            Cancel
                        </Button>
                        <Button
                            onClick={save}
                            disabled={
                                saving || !form.code.trim() || (!isFounder && Number(form.discount_value) <= 0)
                            }
                            data-testid="promo-form-save"
                            className="rounded-full bg-navy-700 hover:bg-navy-800 text-white"
                        >
                            {saving ? "Saving…" : editing ? "Save changes" : "Create promo"}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </>
    );
}

function StatCard({ label, value, accent, testid }) {
    return (
        <div
            data-testid={testid}
            className={`rounded-2xl shadow-soft p-6 ${
                accent ? "bg-navy-700 text-white" : "bg-white"
            }`}
        >
            <div
                className={`text-xs uppercase tracking-[0.3em] mb-2 ${
                    accent ? "text-gold-500" : "text-gold-600"
                }`}
            >
                {label}
            </div>
            <div
                className={`font-display text-4xl ${accent ? "" : "text-navy-700"}`}
            >
                {value}
            </div>
        </div>
    );
}
